/* eslint-disable */

import { Box, Typography, IconButton, Button } from '@mui/material'
import ZoomInIcon from '@mui/icons-material/ZoomIn'
import ZoomOutIcon from '@mui/icons-material/ZoomOut'
import FitScreenIcon from '@mui/icons-material/FitScreen'
import DownloadIcon from '@mui/icons-material/Download'
import { useState, useRef, useEffect, useMemo, useCallback } from 'react'
import { getCellDimensions } from '../utils/cellDimensions'

interface ColorChart {
  id: number;
  name: string;
  description: string | null;
  data: string;
  height: number;
  width: number;
  craft?: string;
  crochet_stitch_type?: string;
}

interface ColorChartWithNumbersProps {
  color_chart: ColorChart;
}

const getColorAt = (parsedData: any, i: number, j: number): string => {
  const cell = parsedData[`${i}_${j}`];
  return cell?.backgroundColor || '#e0e0e0';
}

export default function ColorChartWithNumbers({ color_chart }: ColorChartWithNumbersProps) {
  const [selectedRow, setSelectedRow] = useState<number | null>(null)
  const [zoom, setZoom] = useState<number>(1)
  const [fitToViewScale, setFitToViewScale] = useState<number>(1)
  const [hoveredCell, setHoveredCell] = useState<{ row: number; col: number } | null>(null)
  const [mousePosition, setMousePosition] = useState<{ x: number; y: number } | null>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const rowLabelsRef = useRef<HTMLDivElement>(null);
  const colLabelsRef = useRef<HTMLDivElement>(null);
  const canvasScrollRef = useRef<HTMLDivElement>(null);
  const rowScrollRef = useRef<HTMLDivElement>(null);
  const colScrollRef = useRef<HTMLDivElement>(null);
  const touchStartRef = useRef<{ x: number; y: number; cell: { row: number; col: number; rowIndex: number } | null } | null>(null);
  const lastCellRef = useRef<{ row: number; col: number } | null>(null);

  // Parse JSON once
  const parsedData = useMemo(() => {
    try {
      return JSON.parse(color_chart.data);
    } catch {
      return {};
    }
  }, [color_chart.data]);

  // Get cell dimensions based on craft and stitch type
  const cellDimensions = useMemo(() => {
    const craft = color_chart.craft as 'knitting' | 'crochet' | 'cross_stitch' | null | undefined
    const crochetStitchType = color_chart.crochet_stitch_type as 'sc' | 'hdc' | 'dc' | null | undefined
    return getCellDimensions(craft || null, crochetStitchType || null)
  }, [color_chart.craft, color_chart.crochet_stitch_type])

  // Cell dimensions
  const cellWidth = cellDimensions.width;
  const cellHeight = cellDimensions.height;
  const cellMargin = 0.5; // Increased from 0.25
  const totalCellWidth = cellWidth + cellMargin * 2;
  const totalCellHeight = cellHeight + cellMargin * 2;
  const canvasWidth = color_chart.width * totalCellWidth;
  const canvasHeight = color_chart.height * totalCellHeight;
  const labelWidth = 40; // Increased from 30
  const labelMargin = 1.5; // Increased from 1

  const handleRowClick = useCallback((rowIndex: number) => {
    setSelectedRow(rowIndex === selectedRow ? null : rowIndex);
  }, [selectedRow]);

  // Calculate fit-to-view scale (for the button)
  useEffect(() => {
    const calculateFitToView = () => {
      if (!containerRef.current || !color_chart.width || !color_chart.height) return;
      
      const container = containerRef.current;
      const containerWidth = container.clientWidth - (labelWidth + labelMargin) - 32; // Account for labels and padding
      const containerHeight = window.innerHeight - 200; // Account for header and padding
      
      const chartWidth = canvasWidth;
      const chartHeight = canvasHeight + 40; // Account for column labels
      
      const scaleX = containerWidth / chartWidth;
      const scaleY = containerHeight / chartHeight;
      const newScale = Math.min(scaleX, scaleY, 1) * 0.95; // Don't scale up, leave some margin
      
      setFitToViewScale(newScale);
    };

    calculateFitToView();
    window.addEventListener('resize', calculateFitToView);
    return () => window.removeEventListener('resize', calculateFitToView);
  }, [color_chart.width, color_chart.height, canvasWidth, canvasHeight, labelWidth, labelMargin]);

  // Set initial zoom to fit-to-view
  useEffect(() => {
    if (fitToViewScale > 0) {
      setZoom(fitToViewScale);
    }
  }, [fitToViewScale]);

  const handleFitToView = useCallback(() => {
    setZoom(fitToViewScale);
  }, [fitToViewScale]);

  // Keyboard shortcuts for zoom and row navigation
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // Zoom shortcuts
      if ((e.ctrlKey || e.metaKey) && (e.key === '+' || e.key === '=')) {
        e.preventDefault();
        setZoom(prev => Math.min(1, prev + 0.1));
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.key === '-') {
        e.preventDefault();
        setZoom(prev => Math.max(0.1, prev - 0.1));
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.key === '0') {
        e.preventDefault();
        setZoom(fitToViewScale);
        return;
      }
      
      // Row navigation with arrow keys (only when a row is selected)
      if (selectedRow !== null && !e.ctrlKey && !e.metaKey) {
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          if (selectedRow > 0) {
            setSelectedRow(selectedRow - 1);
          }
          return;
        }
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          if (selectedRow < color_chart.height - 1) {
            setSelectedRow(selectedRow + 1);
          }
          return;
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [fitToViewScale, selectedRow, color_chart.height]);

  // Draw canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size with high DPI support
    const dpr = window.devicePixelRatio || 1;
    canvas.width = canvasWidth * dpr;
    canvas.height = canvasHeight * dpr;
    
    // Disable image smoothing for crisp lines
    ctx.imageSmoothingEnabled = false;
    ctx.imageSmoothingQuality = 'low';
    
    // Scale context for high DPI
    ctx.scale(dpr, dpr);

    // Draw all cells
    for (let i = 0; i < color_chart.height; i++) {
      for (let j = 0; j < color_chart.width; j++) {
        // Flip columns for display
        const colIndex = color_chart.width - 1 - j;
        const x = colIndex * totalCellWidth + cellMargin;
        const y = i * totalCellHeight + cellMargin;

        // Get cell color
        const color = getColorAt(parsedData, i, colIndex);

        // Draw cell background
        ctx.fillStyle = color;
        ctx.fillRect(x, y, cellWidth, cellHeight);
      }
    }

    // Draw selected row border using filled rectangles at exact pixel boundaries
    // Use grey color that matches the background between squares
    if (selectedRow !== null) {
      const y = selectedRow * totalCellHeight;
      const height = totalCellHeight;
      const borderWidth = 3;
      
      // Use grey color that matches the background (#f5f5f5 or similar)
      ctx.fillStyle = '#e0e0e0'; // Grey color matching the default cell color/background
      // Top border
      const topY = Math.round(y);
      ctx.fillRect(0, topY, canvasWidth, borderWidth);
      // Bottom border
      const bottomY = Math.round(y + height - borderWidth);
      ctx.fillRect(0, bottomY, canvasWidth, borderWidth);
    }
  }, [color_chart.width, color_chart.height, parsedData, canvasWidth, canvasHeight, totalCellWidth, totalCellHeight, cellWidth, cellHeight, cellMargin, selectedRow]);

  // Handle canvas click to detect row (accounting for zoom)
  const handleCanvasClick = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const y = (e.clientY - rect.top) / zoom; // Account for zoom transform
    const rowIndex = Math.floor(y / totalCellHeight);
    
    if (rowIndex >= 0 && rowIndex < color_chart.height) {
      handleRowClick(rowIndex);
    }
  }, [color_chart.height, totalCellHeight, zoom, handleRowClick]);

  // Handle canvas mouse move to detect hovered cell
  const handleCanvasMouseMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX - rect.left) / zoom; // Account for zoom transform
    const y = (e.clientY - rect.top) / zoom; // Account for zoom transform
    
    const rowIndex = Math.floor(y / totalCellHeight);
    const colIndex = Math.floor(x / totalCellWidth);
    
    // Flip column index back to match the display (columns are flipped for display)
    const displayColIndex = color_chart.width - 1 - colIndex;
    
    if (rowIndex >= 0 && rowIndex < color_chart.height && colIndex >= 0 && colIndex < color_chart.width) {
      setHoveredCell({ row: color_chart.height - rowIndex, col: displayColIndex + 1 });
      setMousePosition({ x: e.clientX, y: e.clientY });
    } else {
      setHoveredCell(null);
      setMousePosition(null);
    }
  }, [color_chart.width, color_chart.height, totalCellWidth, totalCellHeight, zoom]);

  // Handle canvas mouse leave to clear hover state
  const handleCanvasMouseLeave = useCallback(() => {
    setHoveredCell(null);
    setMousePosition(null);
  }, []);

  // Helper to get cell coordinates from touch/mouse position
  const getCellFromPosition = useCallback((clientX: number, clientY: number): { row: number; col: number; rowIndex: number; colIndex: number } | null => {
    const canvas = canvasRef.current;
    if (!canvas) return null;

    const rect = canvas.getBoundingClientRect();
    const x = (clientX - rect.left) / zoom;
    const y = (clientY - rect.top) / zoom;
    
    const rowIndex = Math.floor(y / totalCellHeight);
    const colIndex = Math.floor(x / totalCellWidth);
    
    // Flip column index back to match the display (columns are flipped for display)
    const displayColIndex = color_chart.width - 1 - colIndex;
    
    if (rowIndex >= 0 && rowIndex < color_chart.height && colIndex >= 0 && colIndex < color_chart.width) {
      return {
        row: color_chart.height - rowIndex,
        col: displayColIndex + 1,
        rowIndex,
        colIndex: displayColIndex
      };
    }
    
    return null;
  }, [color_chart.width, color_chart.height, totalCellWidth, totalCellHeight, zoom]);

  // Handle canvas touch start (for mobile/iPad)
  const handleCanvasTouchStart = useCallback((e: React.TouchEvent<HTMLCanvasElement>) => {
    // Only prevent default for single touch (allow two-finger scrolling and pinch-zoom)
    if (e.touches.length === 1) {
      e.preventDefault();
      e.stopPropagation();
    } else {
      // Multiple touches (pinch-zoom or two-finger scroll) - don't interfere
      touchStartRef.current = null;
      return;
    }
    
    const touch = e.touches[0];
    if (!touch) return;

    const cell = getCellFromPosition(touch.clientX, touch.clientY);
    if (cell) {
      // Store touch start position and cell for tap detection
      touchStartRef.current = {
        x: touch.clientX,
        y: touch.clientY,
        cell: { row: cell.row, col: cell.col, rowIndex: cell.rowIndex }
      };
      lastCellRef.current = { row: cell.row, col: cell.col };
      setHoveredCell({ row: cell.row, col: cell.col });
      setMousePosition({ x: touch.clientX, y: touch.clientY });
    }
  }, [getCellFromPosition]);

  // Handle canvas touch move (for mobile/iPad - only updates tooltip, like mouse move)
  const handleCanvasTouchMove = useCallback((e: React.TouchEvent<HTMLCanvasElement>) => {
    // Only prevent default for single touch (allow two-finger scrolling and pinch-zoom)
    if (e.touches.length === 1 && touchStartRef.current) {
      e.preventDefault();
      e.stopPropagation();
    } else {
      // Multiple touches (pinch-zoom or two-finger scroll) - don't interfere
      touchStartRef.current = null;
      setHoveredCell(null);
      setMousePosition(null);
      return;
    }
    
    const touch = e.touches[0];
    if (!touch) return;

    const cell = getCellFromPosition(touch.clientX, touch.clientY);
    if (cell) {
      // Only update tooltip if we moved to a different cell (like mouse move)
      if (!lastCellRef.current || lastCellRef.current.row !== cell.row || lastCellRef.current.col !== cell.col) {
        lastCellRef.current = { row: cell.row, col: cell.col };
      }
      setHoveredCell({ row: cell.row, col: cell.col });
      setMousePosition({ x: touch.clientX, y: touch.clientY });
    } else {
      setHoveredCell(null);
      setMousePosition(null);
    }
  }, [getCellFromPosition]);

  // Handle canvas touch end (for mobile/iPad)
  const handleCanvasTouchEnd = useCallback((e: React.TouchEvent<HTMLCanvasElement>) => {
    if (e.touches.length === 0) {
      e.preventDefault();
      e.stopPropagation();
      
      // If we have a touch start, check if it was a tap (not a drag)
      if (touchStartRef.current) {
        const touch = e.changedTouches[0];
        if (touch) {
          const moveDistance = Math.sqrt(
            Math.pow(touch.clientX - touchStartRef.current.x, 2) + 
            Math.pow(touch.clientY - touchStartRef.current.y, 2)
          );
          
          // If moved less than 10px, treat as tap and toggle row selection (like click)
          if (moveDistance < 10 && touchStartRef.current.cell) {
            handleRowClick(touchStartRef.current.cell.rowIndex);
          }
        }
      }
      
      touchStartRef.current = null;
      lastCellRef.current = null;
      // Keep hover state briefly, then clear after a short delay
      setTimeout(() => {
        setHoveredCell(null);
        setMousePosition(null);
      }, 100);
    }
  }, [handleRowClick]);

  // Handle canvas touch cancel (for mobile/iPad)
  const handleCanvasTouchCancel = useCallback((e: React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    e.stopPropagation();
    touchStartRef.current = null;
    lastCellRef.current = null;
    setHoveredCell(null);
    setMousePosition(null);
  }, []);

  // Prevent context menu (long-press menu on iOS)
  const handleContextMenu = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    e.stopPropagation();
    return false;
  }, []);

  // Prevent text selection and context menu on canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Prevent context menu (long-press menu)
    const handleContextMenu = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
      return false;
    };

    // Prevent text selection
    const handleSelectStart = (e: Event) => {
      e.preventDefault();
      return false;
    };

    // Prevent drag (which can trigger text selection)
    const handleDragStart = (e: Event) => {
      e.preventDefault();
      return false;
    };

    canvas.addEventListener('contextmenu', handleContextMenu, { passive: false });
    canvas.addEventListener('selectstart', handleSelectStart, { passive: false });
    canvas.addEventListener('dragstart', handleDragStart, { passive: false });

    return () => {
      canvas.removeEventListener('contextmenu', handleContextMenu);
      canvas.removeEventListener('selectstart', handleSelectStart);
      canvas.removeEventListener('dragstart', handleDragStart);
    };
  }, []);

  // Download chart as image
  const handleDownload = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Create a new canvas for export that includes labels
    const exportCanvas = document.createElement('canvas');
    const exportCtx = exportCanvas.getContext('2d');
    if (!exportCtx) return;

    // Calculate dimensions including labels
    const labelWidthTotal = labelWidth + labelMargin;
    const columnHeaderHeight = 40;
    const totalWidth = canvasWidth + labelWidthTotal;
    const totalHeight = canvasHeight + columnHeaderHeight;

    // Set canvas size (use high DPI for better quality)
    const dpr = window.devicePixelRatio || 1;
    exportCanvas.width = totalWidth * dpr;
    exportCanvas.height = totalHeight * dpr;
    exportCtx.scale(dpr, dpr);

    // Disable image smoothing for crisp lines
    exportCtx.imageSmoothingEnabled = false;
    exportCtx.imageSmoothingQuality = 'low';

    // Fill background
    exportCtx.fillStyle = '#ffffff';
    exportCtx.fillRect(0, 0, totalWidth, totalHeight);

    // Draw the chart cells directly to ensure full chart is captured
    // This is more reliable than copying from the canvas
    for (let i = 0; i < color_chart.height; i++) {
      for (let j = 0; j < color_chart.width; j++) {
        // Flip columns for display (same as in the canvas drawing)
        const colIndex = color_chart.width - 1 - j;
        const x = labelWidthTotal + colIndex * totalCellWidth + cellMargin;
        const y = columnHeaderHeight + i * totalCellHeight + cellMargin;
        
        // Get cell color
        const color = getColorAt(parsedData, i, colIndex);
        
        // Draw cell background
        exportCtx.fillStyle = color;
        exportCtx.fillRect(x, y, cellWidth, cellHeight);
      }
    }

    // Draw row labels (Y-axis) - font size should match what's displayed (12px, not scaled)
    exportCtx.fillStyle = '#000000';
    exportCtx.font = '12px sans-serif';
    exportCtx.textAlign = 'right';
    exportCtx.textBaseline = 'middle';
    
    const rowLabelPaddingTop = totalCellHeight / 2 - cellHeight / 2;
    for (let i = 0; i < color_chart.height; i++) {
      const y = columnHeaderHeight + rowLabelPaddingTop + i * totalCellHeight + cellHeight / 2;
      const label = (color_chart.height - i).toString();
      exportCtx.fillText(label, labelWidthTotal - labelMargin, y);
    }

    // Draw column labels (X-axis) - font size should match what's displayed
    exportCtx.textAlign = 'center';
    exportCtx.textBaseline = 'middle';
    
    for (let j = 0; j < color_chart.width; j++) {
      const colIndex = color_chart.width - j;
      const x = labelWidthTotal + j * totalCellWidth + totalCellWidth / 2;
      const y = columnHeaderHeight / 2;
      exportCtx.fillText(colIndex.toString(), x, y);
    }

    // Convert to blob and download
    exportCanvas.toBlob((blob) => {
      if (!blob) return;
      
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${color_chart.name || 'color-chart'}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }, 'image/png');
  }, [canvasWidth, canvasHeight, color_chart.width, color_chart.height, color_chart.name, labelWidth, labelMargin, totalCellWidth, totalCellHeight, cellHeight, cellWidth, cellMargin, parsedData]);

  return (
    <Box 
      ref={containerRef}
      sx={{ 
        padding: 2, 
        display: 'flex', 
        flexDirection: 'column',
        minHeight: 'calc(100vh - 200px)'
      }}
    >
      {/* Zoom Controls */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        gap: 1,
        mb: 2,
        flexShrink: 0
      }}>
        <IconButton 
          size="small" 
          onClick={() => setZoom(prev => Math.max(0.1, prev - 0.1))}
          disabled={zoom <= 0.1}
        >
          <ZoomOutIcon />
        </IconButton>
        <Typography variant="body2" sx={{ minWidth: 60, textAlign: 'center' }}>
          {Math.round(zoom * 100)}%
        </Typography>
        <IconButton 
          size="small" 
          onClick={() => setZoom(prev => Math.min(1, prev + 0.1))}
          disabled={zoom >= 1}
        >
          <ZoomInIcon />
        </IconButton>
        <Button
          size="small"
          variant="outlined"
          startIcon={<FitScreenIcon />}
          onClick={handleFitToView}
          sx={{ ml: 1 }}
        >
          Fit to View
        </Button>
        <Button
          size="small"
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={handleDownload}
          sx={{ ml: 1 }}
        >
          Download Image
        </Button>
      </Box>

      <Box sx={{ 
        display: 'grid',
        gridTemplateColumns: `${labelWidth + labelMargin}px 1fr`,
        gridTemplateRows: `40px 1fr`,
        flexGrow: 1,
        overflow: 'hidden',
        position: 'relative'
      }}>
        {/* Corner spacer (top-left) */}
        <Box sx={{ 
          gridColumn: 1,
          gridRow: 1,
          backgroundColor: 'background.paper',
          zIndex: 3,
          borderRight: '1px solid',
          borderBottom: '1px solid',
          borderColor: 'divider'
        }} />

        {/* Column headers (X-axis) - sticky top */}
        <Box 
          ref={colScrollRef}
          sx={{ 
            gridColumn: 2,
            gridRow: 1,
            overflowX: 'auto',
            overflowY: 'hidden',
            backgroundColor: 'background.paper',
            zIndex: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            WebkitOverflowScrolling: 'touch',
            overscrollBehavior: 'contain'
          }}
          onScroll={(e) => {
            if (canvasScrollRef.current) {
              canvasScrollRef.current.scrollLeft = e.currentTarget.scrollLeft;
            }
          }}
        >
          <Box sx={{
            transform: `scale(${zoom})`,
            transformOrigin: 'top left',
            display: 'flex',
            alignItems: 'center',
            paddingY: 1,
            width: `${canvasWidth + 40}px`,
            height: `${40}px`,
            paddingLeft: `${20}px`,
            paddingRight: `${20}px`
          }}>
            {[...Array(color_chart.width)].map((_, j) => {
              const colIndex = color_chart.width - j;
              return (
                <Box
                  key={`col-${colIndex}`}
                  sx={{
                    width: totalCellWidth,
                    height: cellHeight,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}
                >
                  <Typography variant="caption" sx={{ lineHeight: 1, fontSize: '0.75rem' }}>
                    {colIndex}
                  </Typography>
                </Box>
              )
            })}
          </Box>
        </Box>

        {/* Row headers (Y-axis) - sticky left */}
        <Box 
          ref={rowScrollRef}
          sx={{ 
            gridColumn: 1,
            gridRow: 2,
            overflowY: 'auto',
            overflowX: 'hidden',
            backgroundColor: 'background.paper',
            zIndex: 2,
            borderRight: '1px solid',
            borderColor: 'divider',
            WebkitOverflowScrolling: 'touch',
            overscrollBehavior: 'contain'
          }}
          onScroll={(e) => {
            if (canvasScrollRef.current) {
              canvasScrollRef.current.scrollTop = e.currentTarget.scrollTop;
            }
          }}
        >
          <Box sx={{
            transform: `scale(${zoom})`,
            transformOrigin: 'top left',
            display: 'flex',
            flexDirection: 'column',
            paddingTop: `${totalCellHeight / 2 - cellHeight / 2 + 20}px`,
            height: `${canvasHeight + 40}px`,
            width: `${labelWidth + labelMargin}px`,
            paddingBottom: `${20}px`
          }}>
            {[...Array(color_chart.height)].map((_, i) => (
              <Box
                key={i}
                sx={{
                  height: totalCellHeight,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  paddingRight: labelMargin,
                  flexShrink: 0
                }}
              >
                <Typography variant="caption" sx={{ lineHeight: 1, fontSize: '0.75rem' }}>
                  {color_chart.height - i}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Canvas area - scrollable */}
        <Box 
          ref={canvasScrollRef}
          sx={{ 
            gridColumn: 2,
            gridRow: 2,
            overflow: 'auto',
            position: 'relative',
            WebkitOverflowScrolling: 'touch',
            overscrollBehavior: 'contain',
            userSelect: 'none',
            WebkitUserSelect: 'none'
          }}
          onScroll={(e) => {
            if (rowScrollRef.current) {
              rowScrollRef.current.scrollTop = e.currentTarget.scrollTop;
            }
            if (colScrollRef.current) {
              colScrollRef.current.scrollLeft = e.currentTarget.scrollLeft;
            }
          }}
        >
          <Box sx={{
            width: `${canvasWidth * zoom + 40 * zoom}px`,
            height: `${canvasHeight * zoom + 40 * zoom}px`,
            minWidth: `${canvasWidth * zoom + 40 * zoom}px`,
            minHeight: `${canvasHeight * zoom + 40 * zoom}px`,
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
            padding: `${20 * zoom}px`
          }}>
            <Box sx={{ 
              transform: `scale(${zoom})`, 
              transformOrigin: 'top left',
              willChange: 'transform',
              backfaceVisibility: 'hidden',
              WebkitFontSmoothing: 'antialiased'
            }}>
              <Box sx={{ position: 'relative', userSelect: 'none', WebkitUserSelect: 'none' }}>
                <canvas
                  ref={canvasRef}
                  onClick={handleCanvasClick}
                  onMouseMove={handleCanvasMouseMove}
                  onMouseLeave={handleCanvasMouseLeave}
                  onTouchStart={handleCanvasTouchStart}
                  onTouchMove={handleCanvasTouchMove}
                  onTouchEnd={handleCanvasTouchEnd}
                  onTouchCancel={handleCanvasTouchCancel}
                  onContextMenu={handleContextMenu}
                  style={{
                    display: 'block',
                    width: `${canvasWidth}px`,
                    height: `${canvasHeight}px`,
                    cursor: 'pointer',
                    imageRendering: 'crisp-edges',
                    touchAction: 'pan-x pan-y pinch-zoom',
                    userSelect: 'none',
                    WebkitUserSelect: 'none',
                    WebkitTouchCallout: 'none',
                    WebkitTapHighlightColor: 'transparent',
                    msUserSelect: 'none'
                  } as React.CSSProperties}
                />
                {hoveredCell && mousePosition && (
                  <Box
                    sx={{
                      position: 'fixed',
                      left: mousePosition.x + 8,
                      top: mousePosition.y + 8,
                      backgroundColor: 'rgba(0, 0, 0, 0.8)',
                      color: 'white',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '24px',
                      pointerEvents: 'none',
                      zIndex: 1000,
                      whiteSpace: 'nowrap'
                    }}
                  >
                    Row {hoveredCell.row}, Col {hoveredCell.col}
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
